import { Controller, RegisterOptions, useFormContext } from "react-hook-form";
import { Slider, SliderProps, Typography } from "@kaltura/ds-react-components";
import { Container, StyledFormLabel } from "./CommonStyles";
import { Stack } from "@kaltura/mediaspace-shared-styled";

export interface SliderFieldProps extends Omit<SliderProps, "id" | "ref" | "value" | "onChange" | "onBlur"> {
    id: string;
    label: string;
    required?: boolean;
    description?: string;
    additionalInfo?: string;
    fontWeightBold?: boolean;
    registerOptions?: RegisterOptions;
    inline?: boolean;
    /**
     * Show the value as a label next to the slider.
     * TODO: remove this prop after completing KMS-32886
     */
    showValue?: boolean;
}

/**
 * Slider input field
 */
export const SliderField = (props: SliderFieldProps) => {
    const {
        id,
        label,
        required,
        description,
        defaultValue,
        additionalInfo,
        fontWeightBold,
        registerOptions,
        inline = false,
        className,
        showValue,
        ...sliderProps
    } = props;

    const { control } = useFormContext();

    return (
        <Container inline={inline} className={className}>
            <StyledFormLabel
                htmlFor={id}
                required={required}
                text={label}
                description={description}
                additionalInfo={additionalInfo}
                fontWeightBold={fontWeightBold}
            />
            <div>
                <Controller
                    control={control}
                    name={id}
                    defaultValue={defaultValue}
                    rules={{ required, ...registerOptions }}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                        <Stack direction={"row"} gap={2} alignItems={"center"}>
                            <Slider
                                name={id}
                                ref={ref}
                                value={value}
                                onChange={onChange}
                                onBlur={onBlur}
                                {...sliderProps}
                                slotProps={{
                                    ...sliderProps.slotProps,
                                    input: {
                                        ...sliderProps.slotProps?.input,
                                        id,
                                    },
                                }}
                            />

                            {showValue && (
                                <Typography variant={"body1"}>
                                    {typeof sliderProps.valueLabelFormat === "function"
                                        ? sliderProps.valueLabelFormat(value, 0)
                                        : value}
                                </Typography>
                            )}
                        </Stack>
                    )}
                />
            </div>
        </Container>
    );
};
